import type { QuestionDefinition } from "@reno-estimator/core";
import { KITCHEN_SIZE_RANGES } from "../kitchen-size-ranges.js";

/**
 * Kitchen's full question set (Phase 5A - the reference implementation of
 * a fully-built-out project type on top of the reusable Phase 4 wizard
 * framework). Every one of these renders through the generic
 * `QuestionRenderer`/`ProjectSpecificQuestions` pair in `estimator-app` -
 * nothing here requires a wizard code change, only these question
 * definitions plus the matching pricing line items and rule-calculator
 * mappings in `packages/core/src/calculation/rules/kitchen.ts`.
 *
 * Progressive disclosure: only `kitchen_cabinetry` is required beyond the
 * common questions (location/scope/finish level/etc. - see
 * `common-questions.ts`) - a user can answer the required questions plus
 * a cabinetry choice and get a real, honestly-scoped estimate in well
 * under the 2-4 minute target. Everything else here either defaults to a
 * conservative "excluded/not selected" assumption when skipped (flooring,
 * backsplash, appliances, island, painting) or only appears once the
 * corresponding common yes/no/unknown work-type question says there's
 * relevant work to size (`kitchen_electrical_scope`,
 * `kitchen_plumbing_scope`, `kitchen_structural_scope` - each gated with
 * `displayIf` on `electrical_changes`/`plumbing_changes`/
 * `structural_changes`). Answering "no" to those never shows the
 * follow-up "how much" question at all.
 */

const KITCHEN_SIZE_RANGE_OPTIONS = KITCHEN_SIZE_RANGES.map((r) => ({ id: r.id, label: r.label }));

export const kitchenQuestions: QuestionDefinition[] = [
  {
    id: "kitchen_size_range",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "How large is the kitchen?",
    helpText: "A rough range is fine - you can enter an exact square footage instead if you know it.",
    required: false,
    options: KITCHEN_SIZE_RANGE_OPTIONS,
    order: 15
  },
  {
    id: "kitchen_layout_change",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "Are you keeping the existing kitchen layout?",
    // Per this file's own doc comment above: only kitchen_cabinetry is
    // required beyond the common questions, so a user can get a real
    // estimate from cabinetry alone. This was found set to `required: true`
    // (contradicting that comment and blocking Next until answered) while
    // packaging the estimator for WordPress in Phase 18 - corrected back to
    // optional, matching kitchen's documented progressive-disclosure design
    // and the contradictory-selection warning below, which already handles
    // an unanswered layout choice gracefully.
    required: false,
    options: [
      { id: "keep_layout", label: "Keep the current layout" },
      { id: "minor_changes", label: "Minor changes (e.g. move a sink)" },
      { id: "full_reconfiguration", label: "Full reconfiguration" }
    ],
    order: 200
  },
  {
    id: "kitchen_cabinetry",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What are you doing with cabinetry?",
    required: true,
    options: [
      { id: "keep_existing", label: "Keep existing cabinets", tags: ["materials"] },
      { id: "reface_existing", label: "Reface existing cabinets", tags: ["materials"] },
      { id: "stock_new", label: "Replace with stock cabinets", tags: ["materials"] },
      { id: "semi_custom_new", label: "Replace with semi-custom cabinets", tags: ["materials"] },
      { id: "full_custom_new", label: "Custom cabinetry", tags: ["materials"] }
    ],
    order: 210
  },
  {
    id: "kitchen_countertop",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What countertop material are you considering?",
    required: false,
    options: [
      { id: "keep_existing", label: "Keep existing", tags: ["materials"] },
      { id: "laminate", label: "Laminate", tags: ["materials"] },
      { id: "quartz", label: "Quartz", tags: ["materials"] },
      { id: "granite", label: "Granite", tags: ["materials"] },
      { id: "premium_custom", label: "Premium / custom material", description: "Marble, quartzite, waterfall edges, and similar.", tags: ["materials"] },
      { id: "undecided", label: "Not decided yet" }
    ],
    order: 220
  },
  {
    id: "kitchen_appliances",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What's happening with appliances?",
    required: false,
    options: [
      { id: "keep_existing", label: "Keep existing appliances", tags: ["appliance"] },
      { id: "replace_some", label: "Replace some appliances", tags: ["appliance"] },
      { id: "replace_all", label: "Replace all appliances", tags: ["appliance"] },
      { id: "premium", label: "Premium appliance package", tags: ["appliance"] }
    ],
    order: 230
  },
  {
    id: "kitchen_flooring_type",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What's happening with flooring?",
    required: false,
    options: [
      { id: "not_changing", label: "Not changing" },
      { id: "basic", label: "Basic replacement" },
      { id: "mid_range", label: "Mid-range replacement" },
      { id: "premium", label: "Premium material" }
    ],
    order: 235
  },
  {
    id: "kitchen_backsplash_type",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What's happening with the backsplash?",
    required: false,
    options: [
      { id: "no_change", label: "No change" },
      { id: "standard", label: "Standard backsplash" },
      { id: "full_height", label: "Full-height backsplash" },
      { id: "premium", label: "Premium / custom installation" }
    ],
    order: 238
  },
  {
    id: "kitchen_island",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What about a kitchen island?",
    required: false,
    options: [
      { id: "no_island", label: "No island" },
      { id: "existing_retained", label: "Existing island retained" },
      { id: "new_standard", label: "New standard island" },
      { id: "large_custom", label: "Large / custom island" }
    ],
    order: 240
  },
  {
    id: "kitchen_electrical_scope",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What kind of electrical work does this involve?",
    required: false,
    displayIf: [{ questionId: "electrical_changes", equalsAnyOf: ["yes", "unknown"] }],
    options: [
      { id: "minor_updates", label: "Minor updates" },
      { id: "new_lighting", label: "New lighting" },
      { id: "additional_circuits", label: "Additional outlets / circuits" },
      { id: "significant_changes", label: "Significant electrical changes" }
    ],
    order: 250
  },
  {
    id: "kitchen_plumbing_scope",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What kind of plumbing work does this involve?",
    required: false,
    displayIf: [{ questionId: "plumbing_changes", equalsAnyOf: ["yes", "unknown"] }],
    options: [
      { id: "minor_changes", label: "Minor plumbing changes" },
      { id: "fixtures_relocated", label: "Fixtures are being relocated" },
      { id: "major_changes", label: "Major plumbing changes" }
    ],
    order: 255
  },
  {
    id: "kitchen_structural_scope",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "What kind of structural or layout work does this involve?",
    helpText: "Removing or altering a wall may require an engineer's assessment - see the note on your results.",
    required: false,
    displayIf: [{ questionId: "structural_changes", equalsAnyOf: ["yes", "unknown"] }],
    options: [
      { id: "non_structural_wall_removal", label: "Non-structural wall removal" },
      { id: "potential_structural_wall_removal", label: "Potential structural wall removal" },
      { id: "major_layout_changes", label: "Major layout changes" }
    ],
    order: 260
  },
  {
    id: "kitchen_painting",
    projectTypes: ["kitchen"],
    inputType: "single_select",
    prompt: "Does this include painting?",
    required: false,
    options: [
      { id: "not_included", label: "Not included" },
      { id: "walls_only", label: "Walls only" },
      { id: "full_room", label: "Full room (walls, ceiling, trim)" }
    ],
    order: 270
  }
];
